import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import qs from 'qs';
import { checkAndStoreFcmToken } from 'utils/account/push-notifications';
import { getCookie } from 'utils/bom-dom-manipulation/cookies';
import LocalStorage from 'utils/bom-dom-manipulation/local-storage';
import { getCookieLoginCredentials } from 'utils/bom-dom-manipulation/login-credentials';
import { sendPostMessage } from 'utils/bom-dom-manipulation/send-post-message-to-iframe';
import SessionStorage from 'utils/bom-dom-manipulation/session-storage';
import { VAIX_IFRAME_ID } from 'utils/constants/app/iframe-ids';
import { Key } from 'utils/constants/app/local-storage-keys';
import { SECONDS_PER_MINUTE } from 'utils/constants/dateInfo/time-numbers';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { storageKeyName } from 'utils/generic/storage-key-name';
import { appendCustomCode } from 'utils/other/customCode';
import { ProfileData } from 'interfaces/authentication';
import {
  clearRemainingSessionDurations,
  remainingSessionDuration
} from 'services/account/get-remaining-session-duration';
import { removeUserFromStorage, restoreLogin } from 'services/authentication';
import { FavoritePersonalization } from 'services/favorite-personalization';
import { getUserAllNotifications } from 'services/sportsbook/notifications';
import { useAccountLogout } from './useAccountLogout';
import { useUpdateUser } from './useUpdateUser';
import { TStatus, useFaceIdLogin } from 'components/Login/hooks/useFaceIdLogin';
import {
  addQuery,
  saveCurrentLocation,
  updateQuery
} from 'components/QueryRoute';
import { EPersonalIdTypes } from 'newcomponents/Shared/account/Fields/PersonalIdField/personalIdField.type';
import { useFetchAccountParams } from 'newhooks/account/useFetchAccountParams';
import { setRemainingSessionDurationModal } from 'store/actions/app-data';
import { batchActions } from 'store/actions/batch';
import {
  removeUserData,
  setClientGamingStateInfo,
  setLoginLimit,
  setUserData,
  setUserLoggedIn
} from 'store/actions/user-data';
import { getRegFieldData } from 'store/selectors/app-data';
import { getPartnerConfigs } from 'store/selectors/socket';
import { getIsLoggedIn, getRememberMe } from 'store/selectors/user-data';

export const useRestoreLogin = (
  isFunction = false,
  refreshWhenLoggedIn = false
): Function => {
  const history = useHistory();
  const dispatch = useDispatch();
  const accountLogout = useAccountLogout();
  const isLoggedIn = useSelector(getIsLoggedIn);
  const regFieldsData = useSelector(getRegFieldData);
  // @Todo FIX_MY_TYPE create selector with reselect @todo-account
  const shouldRestore: boolean = useSelector(
    ({ socket }: { socket: { isConnected: boolean } }) => socket.isConnected
  );

  const partnerConfigs = useSelector(getPartnerConfigs);
  const { accountParams } = useFetchAccountParams();
  const rememberMeSelector = useSelector(getRememberMe);

  const { loginWithFaceId } = useFaceIdLogin();
  const { clearDataFromStorage } = FavoritePersonalization();

  const rememberMe = useMemo(() => {
    return +accountParams?.rememberMeTick === 0 || partnerConfigs?.single_login
      ? false
      : rememberMeSelector;
  }, [
    accountParams?.rememberMeTick,
    rememberMeSelector,
    partnerConfigs?.single_login
  ]);

  const { jwe_token } = getCookieLoginCredentials();

  const storeUser = useCallback(
    (userData: ProfileData) => {
      const customCodeAfterLogin: Key[] = [
        'CUSTOM_CODE_REG_AFTER_REFRESH',
        'CUSTOM_CODE_LOGIN_AFTER_REFRESH'
      ];

      customCodeAfterLogin.forEach(elem => {
        const elemCustomCode = LocalStorage.getItem(
          storageKeyName('account', elem)
        );

        if (elem) {
          appendCustomCode(elemCustomCode, userData);
          LocalStorage.removeItem(storageKeyName('account', elem as Key));
        }
      });

      userData.active_time_in_casino &&
        SessionStorage.setItem(
          storageKeyName('account', 'ACTIVE_TIME_IN_CASINO'),
          `${userData.active_time_in_casino}`
        );

      dispatch(
        batchActions(
          setUserData(userData),
          setUserLoggedIn(true),
          setLoginLimit((userData.session_duration || 0) * SECONDS_PER_MINUTE)
        )
      );

      checkAndStoreFcmToken();

      if (SpringConfigs.PUSH_NOTIFICATIONS_ENABLED) {
        getUserAllNotifications();
      }

      sendPostMessage(
        { type: 'userId', value: userData.user_id },
        VAIX_IFRAME_ID
      );
      LocalStorage.removeItem(
        storageKeyName('account', 'SWITCH_MULTI_ACCOUNT')
      );
    },
    [dispatch]
  );

  useEffect(() => {
    const checkRemainingSession =
      !isFunction && Number(accountParams?.sessionLimits || 0);

    if (checkRemainingSession) {
      if (!isLoggedIn) {
        clearRemainingSessionDurations();

        return;
      }

      remainingSessionDuration(
        {
          RemainingDailyDuration: Number(accountParams?.showDailySession || 0),
          RemainingWeeklyDuration: Number(
            accountParams?.showWeeklySession || 0
          ),
          RemainingMonthlyDuration: Number(
            accountParams?.showMonthlySession || 0
          )
        },
        (type: string) => {
          accountLogout(true);

          dispatch(
            setRemainingSessionDurationModal({
              type,
              isOpen: true
            })
          );
        }
      );
    }
  }, [accountParams, isLoggedIn, isFunction]);

  const deleteUser = () => {
    removeUserFromStorage();
    dispatch(removeUserData());
    dispatch(setClientGamingStateInfo(null));

    if (
      SpringConfigs.USER_PERSONALIZATION_ENABLED &&
      !SpringConfigs.MOCKED_DATA
    ) {
      clearDataFromStorage();
    }
  };

  const { updateUser } = useUpdateUser();

  const handle = useCallback(
    (credentials = null) => {
      const auth_data_x = JSON.parse(
        LocalStorage.getItem(storageKeyName('account', 'AUTH_DATA'))
      );

      const mustLogIn = auth_data_x?.auth_token;
      const mustLoginEncrypted = rememberMe && jwe_token;
      const queries =
        credentials ||
        qs.parse(history.location.search, {
          ignoreQueryPrefix: true
        });

      const personalId = regFieldsData?.find(
        field => field && field.formType === 'personalId'
      );

      const isOverridePersonalId = !!(
        personalId &&
        personalId.editForm.showOnEditProfile &&
        personalId.editForm.personalIdType === EPersonalIdTypes.cpf &&
        personalId.editForm.sendCPFAsDocNumber === '1'
      );

      if (queries.auth_token) {
        const customAuthData = {
          auth_token: queries.auth_token as string
        };

        history.replace(
          addQuery({ auth_token: undefined, user_id: undefined })
        );

        restoreLogin(
          customAuthData,
          storeUser,
          updateUser,
          () => deleteUser(),
          isOverridePersonalId,
          true
        );
      } else if (mustLoginEncrypted) {
        restoreLogin(
          { jwe_token: jwe_token, auth_token: mustLogIn },
          storeUser,
          updateUser,
          () => deleteUser(),
          isOverridePersonalId,
          refreshWhenLoggedIn
        );
      } else if (mustLogIn) {
        restoreLogin(
          auth_data_x,
          storeUser,
          updateUser,
          () => deleteUser(),
          isOverridePersonalId,
          refreshWhenLoggedIn
        );
      }

      if (isLoggedIn || mustLogIn || mustLoginEncrypted) {
        if (queries.login || queries.register) {
          saveCurrentLocation();
          history.replace(updateQuery({ accounts: '*', profile: '*' }));
        }
      } else {
        if (queries.settings || queries.profile) {
          saveCurrentLocation();
          history.replace(updateQuery({ accounts: '*', login: '*' }));
        }
      }
    },
    [isLoggedIn, storeUser, rememberMe, regFieldsData]
  );

  useEffect(() => {
    if (regFieldsData && shouldRestore && !isFunction) {
      const isFirstLaunch = getCookie('isWrapperFirstLaunch');

      if (window.median?.auth && isFirstLaunch) {
        window.median.auth.status({
          callbackFunction: (data: TStatus) => {
            if (data && data.hasTouchId && data.hasSecret) {
              loginWithFaceId();
            } else {
              handle();
            }
          },
          prompt: 'PROMPT'
        });
      } else {
        handle();
      }
    }
  }, [shouldRestore, regFieldsData]);

  return handle;
};
